import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Layout } from '../components/Layout'
import { Button } from '../components/ui'
import { useDocumentTitle } from '../lib/useDocumentTitle'
import { APP_PAGE_URL, PRIVACY_URL, TERMS_URL } from '../lib/links'
import homeShot from '../assets/app/home.webp'
import profileShot from '../assets/app/profile.webp'

/**
 * The public face of research.bonded.studio.
 *
 * This is the one route in this app that touches no API. It must stay that way:
 * the survey subtree opens a session the moment it mounts — a POST and an
 * HttpOnly credential cookie — and a visitor reading a hero has consented to
 * nothing. `tests/research.spec.ts` asserts that landing here fires no request.
 *
 * It is also the only route meant to be indexed, which is why index.html no
 * longer carries a static `noindex` and the survey asks for one itself via
 * useRobotsNoindex.
 */
function LegalFooter() {
	const { t } = useTranslation()
	return (
		<footer className="lab-footer">
			<a href={TERMS_URL}>{t('home.terms')}</a>
			<span aria-hidden>·</span>
			<a href={PRIVACY_URL}>{t('home.privacy')}</a>
		</footer>
	)
}

export function MarketingLanding() {
	const { t } = useTranslation()
	const navigate = useNavigate()

	useDocumentTitle(`${t('home.title')} · ${t('brand')}`)

	return (
		<Layout footer={<LegalFooter />}>
			<section className="hero">
				<p className="eyebrow hero-rise" style={{ '--rise': '0ms' } as CSSProperties}>
					{t('home.eyebrow')}
				</p>

				<h1 className="display-title hero-rise" style={{ '--rise': '60ms' } as CSSProperties}>
					{t('home.title')}
				</h1>

				<div className="hero-copy hero-rise" style={{ '--rise': '120ms' } as CSSProperties}>
					<p className="lede">{t('home.ledeResearch')}</p>
					<p className="lede">{t('home.ledeApp')}</p>
				</div>

				<div className="hero-actions hero-rise" style={{ '--rise': '180ms' } as CSSProperties}>
					{/* A real link, not a button with an onClick: /survey has to survive
					    middle-click and open-in-new-tab. The handler is only there to
					    keep the SPA navigation client-side on a plain left click. */}
					<Button
						size="lg"
						href="/survey"
						onClick={(event) => {
							if (event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return
							event.preventDefault()
							navigate('/survey')
						}}
					>
						{t('home.ctaSurvey')}
					</Button>

					<Button size="lg" variant="accent" href={APP_PAGE_URL} target="_blank" rel="noopener">
						{t('home.ctaApp')}
					</Button>
				</div>

				<div className="hero-phones hero-rise" style={{ '--rise': '260ms' } as CSSProperties}>
					{/* The LCP element: eager, high priority, explicit box. */}
					<img
						className="hero-phone hero-phone-a"
						src={homeShot}
						width={700}
						height={1426}
						alt={t('home.phoneAltHome')}
						fetchPriority="high"
						decoding="async"
					/>
					<img
						className="hero-phone hero-phone-b"
						src={profileShot}
						width={700}
						height={1426}
						alt={t('home.phoneAltProfile')}
						loading="lazy"
						decoding="async"
					/>
				</div>
			</section>
		</Layout>
	)
}
